import { useChartSettings } from "@/context/ChartSettingsContext";
import { CustomerRank, CustomerRfmSegment } from "@/enums/customer.enum";

import { useMemo } from "react";

interface DataItem {
  [key: string]: any;
}

export const useFilteredData = <T extends DataItem>(
  data: T[] | undefined,
  dateField: keyof T,
  referenceDate: Date = new Date(),
): T[] => {
  const { timeRange } = useChartSettings();

  return useMemo(() => {
    if (!data) return [];

    let daysToSubtract = 90;
    if (timeRange === "30d") {
      daysToSubtract = 30;
    } else if (timeRange === "7d") {
      daysToSubtract = 7;
    }

    const startDate = referenceDate;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    return data.filter((item) => {
      const date = new Date(item[dateField]);
      return date >= startDate;
    });
  }, [data, timeRange, dateField, referenceDate]);
};

export const createCustomerQueryParamsObject = (
  query: Record<string, any>,
  barcodes?: string[] | undefined,
): Record<string, any> => {
  // 👑
  const queryParams: Record<string, any> = {};

  if (barcodes && barcodes.length > 0) {
    queryParams.barcodes = barcodes;
    return queryParams;
  } else {
    queryParams.query = {};
    // continue
  }

  // convert some query params for backend
  const sortBy = (() => {
    if (query.sortBy === "email") return "username";
    if (query.sortBy === "username") return "nick";
    return query.sortBy;
  })();

  // Pagination & sorting
  if (query.page) queryParams.query.page = query.page;
  if (query.pageSize) queryParams.query.pageSize = query.pageSize;
  if (query.sortBy) queryParams.query.sortBy = sortBy;
  if (query.direction) queryParams.query.direction = query.direction;

  // Filters
  if (query.search) queryParams.query.search = query.search;
  if (query.gender && query.gender !== "both")
    queryParams.query.gender = query.gender;
  if (query.neighbourhoods)
    queryParams.query.neighbourhoods = query.neighbourhoods;
  if (query.minYearOfBirth)
    queryParams.query.minYearOfBirth = query.minYearOfBirth;
  if (query.maxYearOfBirth)
    queryParams.query.maxYearOfBirth = query.maxYearOfBirth;
  if (query.minTransactions)
    queryParams.query.minTransactions = query.minTransactions;
  if (query.maxTransactions)
    queryParams.query.maxTransactions = query.maxTransactions;
  if (query.minSpending) queryParams.query.minSpending = query.minSpending;
  if (query.maxSpending) queryParams.query.maxSpending = query.maxSpending;
  if (query.minBoughtCoupons)
    queryParams.query.minBoughtCoupons = query.minBoughtCoupons;
  if (query.maxBoughtCoupons)
    queryParams.query.maxBoughtCoupons = query.maxBoughtCoupons;
  if (query.minUsedCoupons)
    queryParams.query.minUsedCoupons = query.minUsedCoupons;
  if (query.maxUsedCoupons)
    queryParams.query.maxUsedCoupons = query.maxUsedCoupons;

  return queryParams;
};

export const determineSex = (_sex?: string) => {
  const sex = _sex?.toLocaleLowerCase();

  if (!sex || !["m", "ž"].includes(sex)) {
    return "nedostupno";
  }

  if (sex === "m") {
    return "Muškarac";
  }

  if (sex === "ž") {
    return "Žena";
  }

  return "nedostupno";
};

export const getCustomerInitials = (
  firstName?: string | null,
  surname?: string | null,
  username?: string | null,
) => {
  const initials = [firstName, surname]
    .filter(Boolean)
    .map((namePart) => namePart!.trim().charAt(0).toUpperCase())
    .join("");

  if (initials) return initials;

  return username?.trim().charAt(0).toUpperCase() ?? "";
};

export const RFM_SEGMENT_LABELS: Record<CustomerRfmSegment, string> = {
  [CustomerRfmSegment.CHAMPIONS]: "Najvrijedniji",
  [CustomerRfmSegment.REGULAR]: "Redoviti",
  [CustomerRfmSegment.FORMERLY_ACTIVE]: "Nekad aktivni",
  [CustomerRfmSegment.INACTIVE]: "Neaktivni",
  [CustomerRfmSegment.NEW]: "Novi",
};

export const CUSTOMER_RANK_LABELS: Record<CustomerRank, string> = {
  [CustomerRank.BRONZE]: "Bronze",
  [CustomerRank.SILVER]: "Silver",
  [CustomerRank.GOLD]: "Gold",
};
